/* ==========================================================================
   LUX — Wishlist page

   Renders the saved wishlist on /pages/wishlist.

   The list itself is just an array of product handles in localStorage (see
   assets/lux-wishlist.js). Each card is rendered BY SHOPIFY, not by string
   concatenation here: we request

       /products/<handle>?section_id=lux-wishlist-item

   through the Section Rendering API, which renders sections/lux-wishlist-item
   .liquid with that product in scope. That means prices, currency, variant
   titles, availability and image sizes all come from Liquid — the same source
   of truth as every other page — instead of being re-implemented in JS and
   drifting out of sync (wrong currency symbol, stale price, sold-out items
   still addable).

   Handles that no longer resolve (product deleted, unpublished, or handle
   changed) are pruned from storage rather than rendering a dead card.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.querySelector('[data-lux-wishlist-page]');
  if (!root) return;

  var KEY = (window.LuxWishlist && window.LuxWishlist.key) || 'lux:wishlist';
  var SECTION = 'lux-wishlist-item';

  var grid = root.querySelector('[data-lux-wishlist-grid]');
  var empty = root.querySelector('[data-lux-wishlist-empty]');
  var loading = root.querySelector('[data-lux-wishlist-loading]');
  var summary = root.querySelector('[data-lux-wishlist-summary]');
  var errorEl = root.querySelector('[data-lux-wishlist-error]');

  var rendered = [];   // handles currently painted, in order
  var busy = false;

  /* ---------------------------------------------------------------------
     Storage — read directly so this page works even if the core script
     has not executed yet (defer order is not guaranteed across files).
     --------------------------------------------------------------------- */

  function items() {
    if (window.LuxWishlist && window.LuxWishlist.items) return window.LuxWishlist.items();
    try {
      var parsed = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter(function (h) { return typeof h === 'string' && h; }) : [];
    } catch (e) {
      return [];
    }
  }

  function drop(handle) {
    if (window.LuxWishlist && window.LuxWishlist.remove) return window.LuxWishlist.remove(handle);
    try {
      var list = items();
      var i = list.indexOf(handle);
      if (i === -1) return false;
      list.splice(i, 1);
      window.localStorage.setItem(KEY, JSON.stringify(list));
      return true;
    } catch (e) {
      return false;
    }
  }

  /* Silent prune — used for handles that no longer resolve. Deliberately
     does NOT go through LuxWishlist.remove(), which emits a change event
     that would re-enter render() while we are still inside it. */
  function pruneQuietly(handles) {
    if (!handles.length) return;
    try {
      var list = items().filter(function (h) { return handles.indexOf(h) === -1; });
      window.localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) { /* storage unavailable — nothing to prune */ }
  }

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function productSectionUrl(handle) {
    return routeRoot().replace(/\/$/, '') +
      '/products/' + encodeURIComponent(handle) +
      '?section_id=' + SECTION;
  }

  /* ---------------------------------------------------------------------
     Fetch one card
     --------------------------------------------------------------------- */

  function fetchCard(handle) {
    return fetch(productSectionUrl(handle), {
      headers: { 'Accept': 'text/html' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        if (!res.ok) return { handle: handle, gone: res.status === 404 || res.status === 410 };
        return res.text().then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var card = parsed.querySelector('[data-lux-wishlist-card]');
          // A 200 with no card means the product resolved but is not
          // purchasable/visible in this context — treat it as gone too.
          return { handle: handle, node: card, gone: !card };
        });
      })
      .catch(function () {
        // Network failure — keep the handle, we simply could not show it now.
        return { handle: handle, failed: true };
      });
  }

  /* ---------------------------------------------------------------------
     Render
     --------------------------------------------------------------------- */

  function setSummary(n) {
    if (!summary) return;
    summary.textContent = n === 1 ? '1 item saved' : n + ' items saved';
    summary.hidden = n === 0;
  }

  function showEmpty() {
    if (grid) { grid.innerHTML = ''; grid.hidden = true; }
    if (empty) empty.hidden = false;
    rendered = [];
    setSummary(0);
  }

  function render() {
    if (busy) return Promise.resolve();
    busy = true;

    var handles = items();

    if (errorEl) errorEl.hidden = true;

    if (!handles.length) {
      if (loading) loading.hidden = true;
      showEmpty();
      busy = false;
      return Promise.resolve();
    }

    if (empty) empty.hidden = true;
    if (loading) loading.hidden = false;

    return Promise.all(handles.map(fetchCard))
      .then(function (results) {
        var frag = document.createDocumentFragment();
        var missing = [];
        var failed = 0;
        var order = [];

        results.forEach(function (r) {
          if (r.node) {
            frag.appendChild(r.node);
            order.push(r.handle);
          } else if (r.gone) {
            missing.push(r.handle);
          } else {
            failed++;
          }
        });

        pruneQuietly(missing);

        if (grid) {
          grid.innerHTML = '';
          grid.appendChild(frag);
          grid.hidden = order.length === 0;
        }

        rendered = order;
        if (loading) loading.hidden = true;

        if (order.length === 0) {
          showEmpty();
        } else {
          if (empty) empty.hidden = true;
          setSummary(order.length);
        }

        if (failed > 0 && errorEl) errorEl.hidden = false;

        // Repaint the header count and any heart buttons inside the cards.
        if (window.LuxWishlist && window.LuxWishlist.refresh) window.LuxWishlist.refresh();

        document.dispatchEvent(new CustomEvent('lux:wishlist:rendered', {
          detail: { items: order }
        }));
      })
      .catch(function () {
        if (loading) loading.hidden = true;
        if (errorEl) errorEl.hidden = false;
      })
      .then(function () { busy = false; });
  }

  /* ---------------------------------------------------------------------
     Remove a card
     --------------------------------------------------------------------- */

  // Matched by reading the attribute rather than interpolating the handle
  // into a selector — no escaping rules to get wrong, and no dependency on
  // CSS.escape being available.
  function cardFor(handle) {
    if (!grid) return null;
    var cards = grid.querySelectorAll('[data-lux-wishlist-card]');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].getAttribute('data-product-handle') === handle) return cards[i];
    }
    return null;
  }

  function removeCard(handle) {
    var card = cardFor(handle);

    rendered = rendered.filter(function (h) { return h !== handle; });

    if (card) {
      card.classList.add('is-removing');
      window.setTimeout(function () {
        if (card.parentNode) card.parentNode.removeChild(card);
        if (rendered.length === 0) showEmpty();
      }, 220);
    } else if (rendered.length === 0) {
      showEmpty();
    }

    setSummary(rendered.length);
  }

  root.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-lux-wishlist-remove]');
    if (!btn) return;

    e.preventDefault();

    var handle = btn.getAttribute('data-product-handle');
    if (!handle) return;

    drop(handle);           // emits lux:wishlist:change (handled below)

    if (window.LuxCart && window.LuxCart.toast) {
      window.LuxCart.toast('Removed from your wishlist');
    }
  });

  /* ---------------------------------------------------------------------
     Stay in sync
     --------------------------------------------------------------------- */

  document.addEventListener('lux:wishlist:change', function (e) {
    var detail = e.detail || {};

    // Something was removed while we are on this page — drop just that card
    // instead of re-fetching the whole list.
    if (detail.handle && detail.active === false && rendered.indexOf(detail.handle) !== -1) {
      removeCard(detail.handle);
      return;
    }

    // Anything else (an item added from a quick view on this page, say).
    if (detail.handle && detail.active === true && rendered.indexOf(detail.handle) === -1) {
      render();
    }
  });

  // Another tab changed the list.
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) render();
  });

  // Returning via back/forward from a cached page.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) render();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { render(); });
  } else {
    render();
  }
})();
